import type { IRegDataComparisonResult } from "../../../customTypings/spreadsheetTemplate/SpreadsheetTemplate";
import { WasteReturnRegFieldKeys } from "../../../customTypings/spreadsheetTemplate/SpreadsheetTemplate";
import { getBoolOrFalse } from "./regDataBool";
import { RegDataChangeTypes } from "./regDataAlignmentDisplay";
import type { ReconciliationGridRow } from "./reconciliationGridTypes";
import { ReconciliationRowStates } from "./reconciliationGridTypes";

export const RowVisualStates = {
    Unstaged: "unstaged",
    StagedSync: "staged-sync",
    StagedDelete: "staged-delete",
} as const;

export type RowVisualState = (typeof RowVisualStates)[keyof typeof RowVisualStates];

/** Orange text for pending sync/add changes. */
export const PENDING_CHANGE_TEXT_CLASS = "text-warning";

export interface SoftDeleteUsageHint {
    message: string;
    count: number;
}

export interface SystemDisplayProps {
    visualState: RowVisualState;
    pendingChange: boolean;
    markedForDelete: boolean;
    suppressLifecycleWarning: boolean;
    editedValue: Record<string, unknown> | null;
    useInlineEdit: boolean;
    displayCode?: string;
    displayDescription?: string;
    displayType?: string;
    isHazardous?: boolean;
    isForReceipt?: boolean;
    isForRemoval?: boolean;
    rawDisplayValue?: string;
}

export function resolveRowVisualState(row: ReconciliationGridRow): RowVisualState {
    if (!row.isStaged || !row.stagedChange) {
        return RowVisualStates.Unstaged;
    }

    if (row.stagedChange.changeType === RegDataChangeTypes.SoftDeleteExisting) {
        return RowVisualStates.StagedDelete;
    }

    return RowVisualStates.StagedSync;
}

export function isTemplateColumnPending(row: ReconciliationGridRow): boolean {
    return resolveRowVisualState(row) === RowVisualStates.StagedSync;
}

export function getSoftDeleteUsageHintsForRow(
    row: ReconciliationGridRow,
    comparison: IRegDataComparisonResult | null
): SoftDeleteUsageHint[] {
    const systemRecordId = row.systemSide.systemRecordId;
    if (!systemRecordId) return [];

    const card = comparison?.fields?.find((field) => field.fieldKey === row.fieldKey);
    return (card?.usageHints ?? [])
        .filter((hint) => hint.systemRecordId === systemRecordId)
        .map((hint) => ({ message: hint.message, count: hint.count }));
}

function drFlagsFromEditedValue(editedValue: Record<string, unknown>): {
    isForReceipt: boolean;
    isForRemoval: boolean;
} {
    return {
        isForReceipt: getBoolOrFalse(editedValue, "isForReceipt"),
        isForRemoval: getBoolOrFalse(editedValue, "isForRemoval"),
    };
}

export function buildSystemDisplayProps(row: ReconciliationGridRow): SystemDisplayProps {
    const visualState = resolveRowVisualState(row);
    const option = row.systemSide.option;
    const stagedChange = row.stagedChange;
    const editedValue = stagedChange?.editedValue ?? null;

    if (visualState === RowVisualStates.Unstaged) {
        return {
            visualState,
            pendingChange: false,
            markedForDelete: false,
            suppressLifecycleWarning: false,
            editedValue: null,
            useInlineEdit: false,
            displayCode: option?.code?.trim() ? option.code : row.normalizedKey,
            displayDescription: option?.description ?? undefined,
            rawDisplayValue: option?.displayValue ?? undefined,
        };
    }

    if (visualState === RowVisualStates.StagedDelete) {
        return {
            visualState,
            pendingChange: false,
            markedForDelete: true,
            suppressLifecycleWarning: false,
            editedValue: null,
            useInlineEdit: false,
            displayCode: option?.code?.trim() ? option.code : row.normalizedKey,
            displayDescription: option?.description ?? undefined,
            rawDisplayValue: option?.displayValue ?? undefined,
        };
    }

    const changeType = stagedChange!.changeType;
    const suppressLifecycleWarning =
        changeType === RegDataChangeTypes.ReactivateExisting ||
        changeType === RegDataChangeTypes.RestoreDeleted;

    if (changeType === RegDataChangeTypes.AddNewFromTemplate && editedValue) {
        return {
            visualState,
            pendingChange: true,
            markedForDelete: false,
            suppressLifecycleWarning: true,
            editedValue,
            useInlineEdit: true,
            displayCode: String(editedValue.code ?? "").trim() || undefined,
            displayDescription: String(editedValue.description ?? "").trim() || undefined,
            displayType:
                row.fieldKey === WasteReturnRegFieldKeys.Geography
                    ? String(editedValue.type ?? "").trim() || undefined
                    : undefined,
            isHazardous:
                row.fieldKey === WasteReturnRegFieldKeys.Ewc
                    ? getBoolOrFalse(editedValue, "isHazardous")
                    : undefined,
            ...drFlagsFromEditedValueIfDr(row.fieldKey, editedValue),
        };
    }

    if (!editedValue && !option) {
        return {
            visualState,
            pendingChange: true,
            markedForDelete: false,
            suppressLifecycleWarning,
            editedValue: null,
            useInlineEdit: false,
        };
    }

    const baseCode = option?.code?.trim() ? option.code : row.normalizedKey;
    const baseDescription = option?.description ?? undefined;

    if (changeType === RegDataChangeTypes.SyncFlagsFromTemplate && editedValue) {
        const flags = drFlagsFromEditedValue(editedValue);
        return {
            visualState,
            pendingChange: true,
            markedForDelete: false,
            suppressLifecycleWarning: true,
            editedValue,
            useInlineEdit: false,
            displayCode: String(editedValue.code ?? baseCode ?? "").trim() || baseCode,
            displayDescription: String(editedValue.description ?? baseDescription ?? "").trim() || baseDescription,
            isForReceipt: flags.isForReceipt,
            isForRemoval: flags.isForRemoval,
        };
    }

    if (changeType === RegDataChangeTypes.SyncHazardousFromTemplate && editedValue) {
        return {
            visualState,
            pendingChange: true,
            markedForDelete: false,
            suppressLifecycleWarning: true,
            editedValue,
            useInlineEdit: false,
            displayCode: String(editedValue.code ?? baseCode ?? "").trim() || baseCode,
            displayDescription: String(editedValue.description ?? baseDescription ?? "").trim() || baseDescription,
            isHazardous: getBoolOrFalse(editedValue, "isHazardous"),
        };
    }

    return {
        visualState,
        pendingChange: true,
        markedForDelete: false,
        suppressLifecycleWarning,
        editedValue,
        useInlineEdit: false,
        displayCode: baseCode,
        displayDescription: baseDescription,
        rawDisplayValue: option?.displayValue ?? undefined,
    };
}

function drFlagsFromEditedValueIfDr(
    fieldKey: string,
    editedValue: Record<string, unknown>
): Pick<SystemDisplayProps, "isForReceipt" | "isForRemoval"> {
    if (fieldKey !== WasteReturnRegFieldKeys.EaDr) {
        return {};
    }
    return drFlagsFromEditedValue(editedValue);
}

export function shouldShowSoftDeleteUsageHints(row: ReconciliationGridRow): boolean {
    return resolveRowVisualState(row) === RowVisualStates.StagedDelete;
}

export function isMatchRow(row: ReconciliationGridRow): boolean {
    return row.rowState === ReconciliationRowStates.Match;
}
